import { Injectable, inject } from '@angular/core';
import { Bundle, Coding, Identifier, Patient } from 'fhir/r4';
import { terminologyCanonicalUrlBaseToken } from '../../app.config';
import { TenantMetadataService } from '../../shared/tenant-metadata.service';
import { ReferredPatientShellArtifacts } from './referral-workflow.models';

export interface ReferredPatientShellInput {
    sourcePatient: Patient;
    destinationOrganizationId: string;
    destinationOrganizationDisplay?: string;
    sourceOrganizationId?: string;
    sourceOrganizationDisplay?: string;
    referralIdentifier: string;
}

@Injectable({ providedIn: 'root' })
export class ReferredPatientShellService {
    private readonly terminologyBaseUrl = inject(terminologyCanonicalUrlBaseToken);
    private readonly tenantMetadata = inject(TenantMetadataService);
    private readonly originPatientSystem = `${this.terminologyBaseUrl}/NamingSystem/origin-patient`;
    private readonly referralLinkSystem = `${this.terminologyBaseUrl}/NamingSystem/referral-identifier`;
    private readonly sourceOrganizationSecuritySystem = `${this.terminologyBaseUrl}/CodeSystem/organizationTenant`;

    buildArtifacts(input: ReferredPatientShellInput): ReferredPatientShellArtifacts {
        const patient = this.buildShellPatient(input);

        return {
            patient,
            transactionBundle: {
                resourceType: 'Bundle',
                type: 'transaction',
                entry: [
                    {
                        fullUrl: this.makeUrn(),
                        resource: patient,
                        request: {
                            method: 'POST',
                            url: 'Patient'
                        }
                    }
                ]
            } as Bundle
        };
    }

    private buildShellPatient(input: ReferredPatientShellInput): Patient {
        const sourcePatient = input.sourcePatient;
        const originReference = sourcePatient.id ? `Patient/${sourcePatient.id}` : undefined;
        const identifiers: Identifier[] = [
            {
                system: this.referralLinkSystem,
                value: input.referralIdentifier,
                type: {
                    text: 'Referral Identifier'
                }
            }
        ];

        if (sourcePatient.id) {
            identifiers.push({
                system: this.originPatientSystem,
                value: sourcePatient.id,
                assigner: input.sourceOrganizationId
                    ? {
                        reference: `Organization/${input.sourceOrganizationId}`,
                        display: input.sourceOrganizationDisplay?.trim() || undefined
                    }
                    : undefined,
                type: {
                    text: 'Origin Patient Record'
                }
            });
        }

        const shell: Patient = {
            resourceType: 'Patient',
            active: true,
            identifier: identifiers,
            name: sourcePatient.name,
            telecom: sourcePatient.telecom,
            gender: sourcePatient.gender,
            birthDate: sourcePatient.birthDate,
            address: sourcePatient.address,
            managingOrganization: {
                reference: `Organization/${input.destinationOrganizationId}`,
                display: input.destinationOrganizationDisplay?.trim() || undefined
            },
            link: originReference
                ? [
                    {
                        other: {
                            reference: originReference,
                            display: this.displayName(sourcePatient)
                        },
                        type: 'seealso'
                    }
                ]
                : undefined,
            meta: {
                security: this.buildSourceSecurity(input.sourceOrganizationId, input.sourceOrganizationDisplay)
            }
        };

        this.tenantMetadata.ensureTenantTag(
            shell,
            input.destinationOrganizationId,
            input.destinationOrganizationDisplay
        );

        return shell;
    }

    private buildSourceSecurity(sourceOrganizationId?: string, sourceOrganizationDisplay?: string): Coding[] | undefined {
        const normalizedId = sourceOrganizationId?.trim();
        if (!normalizedId) {
            return undefined;
        }

        return [
            {
                system: this.sourceOrganizationSecuritySystem,
                code: normalizedId,
                display: sourceOrganizationDisplay?.trim() || undefined
            }
        ];
    }

    private displayName(patient: Patient): string {
        const name = patient.name?.[0];
        const given = name?.given?.join(' ') ?? '';
        const family = name?.family ?? '';
        return [given, family].filter(Boolean).join(' ').trim() || patient.id || 'Patient';
    }

    private makeUrn(): string {
        const suffix = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.round(Math.random() * 1_000_000)}`;

        return `urn:uuid:${suffix}`;
    }
}